import { create } from 'zustand';

interface User {
  id: string;
  email: string;
  name: string;
  tenantId: string;
  role: 'superadmin' | 'admin';
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isInitialized: boolean;

  login: (user: User, token: string, refreshToken: string) => void;
  setTokens: (token: string, refreshToken: string) => void;
  logout: () => void;
  setUser: (user: User | null) => void;
  setInitialized: (val: boolean) => void;
}

const TOKEN_KEY = 'token';
const REFRESH_TOKEN_KEY = 'refreshToken';

const readStored = (key: string): string | null => {
  const value = localStorage.getItem(key);
  if (!value || value === 'null' || value === 'undefined') return null;
  return value;
};

// localStorage is the source of truth for tokens, not the store snapshot.
// Another tab can rotate them at any moment (refresh tokens are one-time-use
// server-side), so anything that *sends* a token must read it at call time —
// a stale in-memory copy gets rejected as a reused token and kills the session
// in every tab.
export const getAccessToken = (): string | null => readStored(TOKEN_KEY);
export const getRefreshToken = (): string | null => readStored(REFRESH_TOKEN_KEY);

export const useAuthStore = create<AuthState>(set => ({
  user: null,
  token: readStored(TOKEN_KEY),
  refreshToken: readStored(REFRESH_TOKEN_KEY),
  isInitialized: false,

  login: (user, token, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    set({ user, token, refreshToken, isInitialized: true });
  },

  // Update tokens after a silent refresh without touching the user object.
  setTokens: (token, refreshToken) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    set({ token, refreshToken });
  },

  logout: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    set({ user: null, token: null, refreshToken: null, isInitialized: true });
  },

  setUser: user => {
    set({ user });
    if (!user) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      set({ token: null, refreshToken: null });
    }
  },

  setInitialized: val => {
    set({ isInitialized: val });
  },
}));

// Cross-tab sync. `storage` only fires in the *other* tabs, so this mirrors a
// sibling tab's rotation or logout into this tab's state instead of letting it
// drift. Without it, logging out in one tab leaves the others rendering a
// dashboard whose every request 401s.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', event => {
    if (event.key !== TOKEN_KEY && event.key !== REFRESH_TOKEN_KEY) return;

    const token = readStored(TOKEN_KEY);
    const refreshToken = readStored(REFRESH_TOKEN_KEY);

    if (!token && !refreshToken) {
      useAuthStore.setState({ user: null, token: null, refreshToken: null });
      return;
    }

    useAuthStore.setState({ token, refreshToken });
  });
}
